import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MotionConfig, MotionGlobalConfig } from 'framer-motion';

export const ANIMATIONS_STORAGE_KEY = 'user_animations_enabled';
export const HAPTICS_STORAGE_KEY = 'user_haptics_enabled';
export const PUSH_VIBRATION_STORAGE_KEY = 'user_push_vibration_enabled';
export const BROWSER_NOTIFICATIONS_STORAGE_KEY = 'user_browser_notifications_enabled';

const readStoredBoolean = (key, fallback = true) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value !== 'false';
  } catch {
    return fallback;
  }
};

const readReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const persistBoolean = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(Boolean(value)));
  } catch {
    // Private browsing and storage policies may block localStorage.
  }
};

const syncPushVibrationWithServiceWorker = (enabled) => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const message = { type: 'SET_PUSH_VIBRATION_ENABLED', enabled: Boolean(enabled) };

  navigator.serviceWorker.controller?.postMessage(message);
  navigator.serviceWorker.ready
    .then((registration) => registration.active?.postMessage(message))
    .catch(() => {});
};

const InteractionPreferencesContext = createContext(null);

export const InteractionPreferencesProvider = ({ children }) => {
  const [userAnimationsEnabled, setUserAnimationsEnabledState] = useState(
    () => readStoredBoolean(ANIMATIONS_STORAGE_KEY, true)
  );
  const [hapticsEnabled, setHapticsEnabledState] = useState(
    () => readStoredBoolean(HAPTICS_STORAGE_KEY, true)
  );
  const [pushVibrationEnabled, setPushVibrationEnabledState] = useState(
    () => readStoredBoolean(PUSH_VIBRATION_STORAGE_KEY, true)
  );
  const [browserNotificationsEnabled, setBrowserNotificationsEnabledState] = useState(
    () => readStoredBoolean(BROWSER_NOTIFICATIONS_STORAGE_KEY, true)
  );
  const [reducedMotionRequested, setReducedMotionRequested] = useState(readReducedMotion);

  const animationsEnabled = userAnimationsEnabled && !reducedMotionRequested;
  MotionGlobalConfig.skipAnimations = !animationsEnabled;

  const setUserAnimationsEnabled = useCallback((enabled) => {
    const nextValue = Boolean(enabled);
    persistBoolean(ANIMATIONS_STORAGE_KEY, nextValue);
    setUserAnimationsEnabledState(nextValue);
  }, []);

  const setHapticsEnabled = useCallback((enabled) => {
    const nextValue = Boolean(enabled);
    persistBoolean(HAPTICS_STORAGE_KEY, nextValue);
    setHapticsEnabledState(nextValue);
  }, []);

  const setPushVibrationEnabled = useCallback((enabled) => {
    const nextValue = Boolean(enabled);
    persistBoolean(PUSH_VIBRATION_STORAGE_KEY, nextValue);
    setPushVibrationEnabledState(nextValue);
    syncPushVibrationWithServiceWorker(nextValue);
  }, []);

  const setBrowserNotificationsEnabled = useCallback((enabled) => {
    const nextValue = Boolean(enabled);
    persistBoolean(BROWSER_NOTIFICATIONS_STORAGE_KEY, nextValue);
    setBrowserNotificationsEnabledState(nextValue);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) return undefined;

    const handleChange = (event) => setReducedMotionRequested(event.matches);
    setReducedMotionRequested(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', handleChange);
    mediaQuery.addListener?.(handleChange);

    return () => {
      mediaQuery.removeEventListener?.('change', handleChange);
      mediaQuery.removeListener?.(handleChange);
    };
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === ANIMATIONS_STORAGE_KEY) {
        setUserAnimationsEnabledState(readStoredBoolean(ANIMATIONS_STORAGE_KEY, true));
      } else if (event.key === HAPTICS_STORAGE_KEY) {
        setHapticsEnabledState(readStoredBoolean(HAPTICS_STORAGE_KEY, true));
      } else if (event.key === PUSH_VIBRATION_STORAGE_KEY) {
        const nextValue = readStoredBoolean(PUSH_VIBRATION_STORAGE_KEY, true);
        setPushVibrationEnabledState(nextValue);
        syncPushVibrationWithServiceWorker(nextValue);
      } else if (event.key === BROWSER_NOTIFICATIONS_STORAGE_KEY) {
        setBrowserNotificationsEnabledState(readStoredBoolean(BROWSER_NOTIFICATIONS_STORAGE_KEY, true));
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    MotionGlobalConfig.skipAnimations = !animationsEnabled;
    root.dataset.motion = animationsEnabled ? 'on' : 'off';
    body.classList.toggle('animations-enabled', animationsEnabled);
  }, [animationsEnabled]);

  useEffect(() => {
    syncPushVibrationWithServiceWorker(pushVibrationEnabled);

    const handleControllerChange = () => {
      syncPushVibrationWithServiceWorker(pushVibrationEnabled);
    };
    navigator.serviceWorker?.addEventListener?.('controllerchange', handleControllerChange);
    return () => navigator.serviceWorker?.removeEventListener?.('controllerchange', handleControllerChange);
  }, [pushVibrationEnabled]);

  const value = useMemo(() => ({
    animationsEnabled,
    userAnimationsEnabled,
    reducedMotionRequested,
    setUserAnimationsEnabled,
    hapticsEnabled,
    setHapticsEnabled,
    pushVibrationEnabled,
    setPushVibrationEnabled,
    browserNotificationsEnabled,
    setBrowserNotificationsEnabled,
  }), [
    animationsEnabled,
    userAnimationsEnabled,
    reducedMotionRequested,
    setUserAnimationsEnabled,
    hapticsEnabled,
    setHapticsEnabled,
    pushVibrationEnabled,
    setPushVibrationEnabled,
    browserNotificationsEnabled,
    setBrowserNotificationsEnabled,
  ]);

  return (
    <InteractionPreferencesContext.Provider value={value}>
      <MotionConfig
        reducedMotion={animationsEnabled ? 'never' : 'always'}
        transition={animationsEnabled ? undefined : { duration: 0 }}
      >
        {children}
      </MotionConfig>
    </InteractionPreferencesContext.Provider>
  );
};

export const useInteractionPreferences = () => {
  const context = useContext(InteractionPreferencesContext);
  if (!context) {
    return {
      animationsEnabled: true,
      hapticsEnabled: true,
      setAnimationsEnabled: () => {},
      setHapticsEnabled: () => {},
      resetInteractionPreferences: () => {},
    };
  }
  return context;
};

